import { useEffect, useRef, useCallback } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export interface MemberProfile {
  id: string;
  name: string;
  primaryRole: string;
  secondaryRoles?: string[];
  committees?: string[];
  image?: string;
  bio: string;
}

interface ProfileModalProps {
  member: MemberProfile | null;
  isOpen: boolean;
  onClose: () => void;
}

const ProfileModal = ({ member, isOpen, onClose }: ProfileModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  // Store the trigger element when modal opens
  useEffect(() => {
    if (isOpen) {
      triggerRef.current = document.activeElement as HTMLElement;
    }
  }, [isOpen]);

  // Focus trap and keyboard handling
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === "Escape") {
      onClose();
      return;
    }

    if (e.key === "Tab" && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    }
  }, [onClose]);

  // Set up event listeners and body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
      
      // Focus the close button when modal opens
      setTimeout(() => closeButtonRef.current?.focus(), 10);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
      
      // Return focus to trigger element
      if (!isOpen && triggerRef.current) {
        triggerRef.current.focus();
      }
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !member) return null;

  const bioLines = member.bio.split('\n').filter(line => line.trim());

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />
      
      {/* Modal */}
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-modal-title"
        className="relative w-full max-w-3xl max-h-[90vh] bg-card rounded-xl shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="relative bg-gradient-hero p-6 md:p-8 text-center">
          <Button
            ref={closeButtonRef}
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-primary-foreground hover:bg-primary-foreground/20 min-h-[44px] min-w-[44px]"
            onClick={onClose}
            aria-label="Close profile"
          >
            <X className="w-6 h-6" />
          </Button>

          {/* Avatar */}
          <div className="w-32 h-32 md:w-40 md:h-40 rounded-full mx-auto mb-4 overflow-hidden border-4 border-primary-foreground/30 bg-muted">
            {member.image ? (
              <img
                src={member.image}
                alt={`Profile photo of ${member.name}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-primary flex items-center justify-center">
                <span className="text-4xl md:text-5xl font-bold text-primary-foreground">
                  {member.name.charAt(0)}
                </span>
              </div>
            )}
          </div>

          {/* Name */}
          <h2
            id="profile-modal-title"
            className="text-2xl md:text-3xl font-bold text-primary-foreground mb-3"
          >
            {member.name}
          </h2>

          {/* Role chips */}
          <div className="flex flex-wrap justify-center gap-2">
            <Badge className="bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30 text-sm">
              {member.primaryRole}
            </Badge>
            {member.secondaryRoles?.map((role, idx) => (
              <Badge
                key={idx}
                variant="outline"
                className="border-primary-foreground/40 text-primary-foreground/90 text-sm"
              >
                {role}
              </Badge>
            ))}
          </div>

          {/* Committees */}
          {member.committees && member.committees.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2 mt-3">
              {member.committees.map((committee, idx) => (
                <Badge
                  key={idx}
                  variant="secondary"
                  className="bg-primary-foreground/10 text-primary-foreground/80 text-xs"
                >
                  {committee}
                </Badge>
              ))}
            </div>
          )}
        </header>

        {/* Body - scrollable */}
        <article className="flex-1 overflow-y-auto p-6 md:p-8">
          <div className="prose prose-lg max-w-none text-card-foreground">
            {bioLines.map((paragraph, idx) => (
              <p
                key={idx}
                className="text-base md:text-lg leading-relaxed mb-4 last:mb-0 text-muted-foreground"
              >
                {paragraph}
              </p>
            ))}
          </div>
        </article>

        {/* Footer */}
        <footer className="p-4 border-t border-border flex justify-center">
          <Button
            onClick={onClose}
            className="min-h-[44px] px-8"
          >
            Close
          </Button>
        </footer>
      </div>
    </div>
  );
};

export default ProfileModal;
